import { useMemo, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { canSeePricingConsole } from '../../../../lib/capabilities.js';
import { cn } from '../../../../lib/cn.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { PricingDetailPanel } from '../../../../components/admin/PricingDetailPanel.js';
import { usePricingCourses, type PricingCourseRow } from '../../../../hooks/usePricing.js';
import { matchesCourseQuery } from '../../../../lib/pricing.js';
import { centsToAudDisplay } from '../../../../lib/money.js';
import { formatDate } from '../../../../lib/format.js';
import { Input } from '../../../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/pricing',
  // Gates on the edit_pricing CAPABILITY (Brief 8b) — the same audience as the
  // admin/super_admin role check it replaces, since that switch is exactly those
  // two presets, but it now follows the switch. edit_ and not view_pricing: this
  // console is an unconditional editor, so a view-only holder would meet a
  // screen whose every Save 403s. No tenant condition — it is a config route,
  // and resolveEffectiveTenant maps super_admin to the primary tenant.
  beforeLoad: () => {
    if (!canSeePricingConsole(useAuthStore.getState().user)) throw redirect({ to: '/app' });
  },
  component: PricingPage,
});

function PricingPage() {
  const { data, isLoading, error, refetch } = usePricingCourses();
  const items = data?.items ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = items.find((c) => c.courseId === selectedId) ?? null;

  return (
    <div>
      <AdminPageHeader
        title="Course Pricing"
        description="Set your agency's fee for each course. The college's published fee is shown for reference, and every change is kept in the history."
      />
      {data?.asOf && (
        <p className="mb-4 text-xs text-muted">Agency fees current as of {formatDate(data.asOf)}.</p>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start">
        <PricingList
          items={items}
          isLoading={isLoading}
          error={(error as Error | null) ?? null}
          onRetry={() => refetch()}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selected ? (
          <PricingDetailPanel key={selected.courseId} course={selected} />
        ) : (
          <div className="surface p-10 text-center text-sm text-muted">
            Select a course to view and set its fee.
          </div>
        )}
      </div>
    </div>
  );
}

function PricingList({
  items,
  isLoading,
  error,
  onRetry,
  selectedId,
  onSelect,
}: {
  items: PricingCourseRow[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      items
        .filter((c) => matchesCourseQuery(c, search))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [items, search],
  );

  return (
    <div className="surface p-4">
      <Input
        placeholder="Search course or college…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading courses…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn't load courses: {error.message}{' '}
            <button className="underline" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No courses match.</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <ul className="divide-y divide-line">
            {filtered.map((c) => (
              <li key={c.courseId}>
                <button
                  onClick={() => onSelect(c.courseId)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors',
                    selectedId === c.courseId ? 'bg-cream-deep/60' : 'hover:bg-cream/50',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{c.name}</span>
                    <span className="block truncate text-xs text-muted">{c.providerName}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    {c.agencyFee ? (
                      <span className="block text-sm font-medium text-navy">
                        {centsToAudDisplay(c.agencyFee.tuitionCents)}
                      </span>
                    ) : (
                      <span className="badge bg-cream-deep/60 text-muted">Not set yet</span>
                    )}
                    <span className="block text-[11px] text-muted">
                      Published: {centsToAudDisplay(c.publishedAnnualTuitionCents)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
